// Admin API service - connects to the auth-api microservice for admin functions
const AUTH_API_BASE =
  import.meta.env.VITE_AUTH_API_URL || "http://localhost:8081";

// Generate a correlation ID for request tracing
function generateCorrelationId(): string {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).substring(2, 11)}`;
}

interface ApiError {
  error: string;
}

async function adminRequest<T>(
  endpoint: string,
  options: RequestInit = {},
): Promise<T> {
  const authData = localStorage.getItem("tradecraft-auth");
  const token = authData ? JSON.parse(authData).state?.token : null;

  const correlationId = generateCorrelationId();

  const headers: HeadersInit = {
    "Content-Type": "application/json",
    "X-Correlation-ID": correlationId,
    ...(token && { Authorization: `Bearer ${token}` }),
    ...options.headers,
  };

  const response = await fetch(`${AUTH_API_BASE}${endpoint}`, {
    ...options,
    headers,
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error((data as ApiError).error || "Request failed");
  }

  return data as T;
}

// Types
export type ActivityCategory = "auth" | "strategy" | "portfolio" | "brokerage" | "system";

export interface ActivityLogEntry {
  id: string;
  correlation_id: string;
  user_id: string | null;
  username: string | null;
  email: string | null;
  activity_type: string;
  category: ActivityCategory;
  title: string;
  description: string;
  metadata: Record<string, unknown>;
  ip_address: string;
  user_agent: string;
  created_at: string;
}

export interface ActivityLogListResponse {
  activities: ActivityLogEntry[];
  total: number;
  page: number;
  limit: number;
  pages: number;
}

export interface ActivityLogFilter {
  user_id?: string;
  username?: string;
  email?: string;
  category?: ActivityCategory[];
  activity_type?: string[];
  correlation_id?: string;
  from?: string; // YYYY-MM-DD
  to?: string; // YYYY-MM-DD
  page?: number;
  limit?: number;
}

export interface ActivityLogStats {
  total: number;
  today: number;
  by_category: Record<ActivityCategory, number>;
  oldest?: string | null;
}

// Build query string from filter
function buildQueryString(filter: ActivityLogFilter): string {
  const params = new URLSearchParams();

  if (filter.user_id) params.append("user_id", filter.user_id);
  if (filter.username) params.append("username", filter.username);
  if (filter.email) params.append("email", filter.email);
  if (filter.category?.length) params.append("category", filter.category.join(","));
  if (filter.activity_type?.length) params.append("activity_type", filter.activity_type.join(","));
  if (filter.correlation_id) params.append("correlation_id", filter.correlation_id);
  if (filter.from) params.append("from", filter.from);
  if (filter.to) params.append("to", filter.to);
  if (filter.page) params.append("page", filter.page.toString());
  if (filter.limit) params.append("limit", filter.limit.toString());

  const queryString = params.toString();
  return queryString ? `?${queryString}` : "";
}

// API Functions

/**
 * List activity logs with optional filtering and pagination
 */
export async function listActivityLogs(
  filter: ActivityLogFilter = {},
): Promise<ActivityLogListResponse> {
  const queryString = buildQueryString(filter);
  return adminRequest<ActivityLogListResponse>(`/api/v1/admin/activity-logs${queryString}`);
}

/**
 * Get all activity logs for a specific correlation ID
 */
export async function getActivityLogsByCorrelationId(
  correlationId: string,
): Promise<ActivityLogEntry[]> {
  const response = await adminRequest<{ activities: ActivityLogEntry[] }>(
    `/api/v1/admin/activity-logs/correlation/${encodeURIComponent(correlationId)}`,
  );
  return response.activities;
}

/**
 * Get activity log statistics
 */
export async function getActivityLogStats(): Promise<ActivityLogStats> {
  return adminRequest<ActivityLogStats>("/api/v1/admin/activity-logs/stats");
}

/**
 * Export activity logs as CSV
 */
export async function exportActivityLogs(filter: ActivityLogFilter = {}): Promise<Blob> {
  const queryString = buildQueryString(filter);
  const authData = localStorage.getItem("tradecraft-auth");
  const token = authData ? JSON.parse(authData).state?.token : null;

  const response = await fetch(`${AUTH_API_BASE}/api/v1/admin/activity-logs/export${queryString}`, {
    headers: {
      "X-Correlation-ID": generateCorrelationId(),
      ...(token && { Authorization: `Bearer ${token}` }),
    },
  });

  if (!response.ok) {
    throw new Error("Failed to export activity logs");
  }

  return response.blob();
}

/**
 * Cleanup old activity logs (admin only)
 * @param daysToKeep Number of days of logs to keep (default: 90)
 */
export async function cleanupActivityLogs(
  daysToKeep: number = 90,
): Promise<{ deleted: number; message: string }> {
  return adminRequest<{ deleted: number; message: string }>(
    `/api/v1/admin/activity-logs/cleanup?days_to_keep=${daysToKeep}`,
    { method: "DELETE" },
  );
}

// Trading Config Types
export interface TradingConfig {
  id: string;
  use_gtc_orders: boolean;
  bypass_market_hours: boolean;
  skip_order_fill_wait: boolean;
  dry_run_mode: boolean;
  updated_at: string;
  updated_by: string | null;
}

export interface TradingConfigUpdate {
  use_gtc_orders?: boolean;
  bypass_market_hours?: boolean;
  skip_order_fill_wait?: boolean;
  dry_run_mode?: boolean;
}

// Trading Config API Methods
export async function getTradingConfig(): Promise<TradingConfig> {
  return adminRequest<TradingConfig>("/api/v1/admin/trading/config");
}

export async function updateTradingConfig(
  update: TradingConfigUpdate,
): Promise<TradingConfig> {
  return adminRequest<TradingConfig>("/api/v1/admin/trading/config", {
    method: "PUT",
    body: JSON.stringify(update),
  });
}

// ==========================================
// Ticker Universe Types and APIs
// ==========================================

export type TickerStatus = "active" | "inactive";
export type TickerSource = "seed" | "discovered" | "manual";

export interface TickerUniverse {
  id: number;
  ticker: string;
  status: TickerStatus;
  source: TickerSource;
  fail_count: number;
  last_warmed_at?: string;
  last_failed_at?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

export interface TickerUniverseListResponse {
  tickers: TickerUniverse[];
  total: number;
}

export interface TickerUniverseStats {
  total_tickers: number;
  active_tickers: number;
  inactive_tickers: number;
  seed_tickers: number;
  discovered_tickers: number;
  manual_tickers: number;
  tickers_with_failures: number;
}

// Ticker Universe API Methods
export async function listTickers(
  status?: TickerStatus,
  source?: TickerSource,
): Promise<TickerUniverseListResponse> {
  const params = new URLSearchParams();
  if (status) params.append("status", status);
  if (source) params.append("source", source);
  const queryString = params.toString();
  return adminRequest<TickerUniverseListResponse>(
    `/api/v1/admin/tickers${queryString ? `?${queryString}` : ""}`,
  );
}

export async function getTickerStats(): Promise<TickerUniverseStats> {
  return adminRequest<TickerUniverseStats>("/api/v1/admin/tickers/stats");
}

export async function createTicker(
  ticker: string,
  notes?: string,
): Promise<TickerUniverse> {
  return adminRequest<TickerUniverse>("/api/v1/admin/tickers", {
    method: "POST",
    body: JSON.stringify({ ticker, notes }),
  });
}

export async function updateTickerStatus(
  ticker: string,
  status: TickerStatus,
  notes?: string,
): Promise<TickerUniverse> {
  return adminRequest<TickerUniverse>(`/api/v1/admin/tickers/${ticker}/status`, {
    method: "PUT",
    body: JSON.stringify({ status, notes }),
  });
}

export async function deleteTicker(ticker: string): Promise<void> {
  await adminRequest<void>(`/api/v1/admin/tickers/${ticker}`, {
    method: "DELETE",
  });
}

// Category display names
export const CATEGORY_DISPLAY_NAMES: Record<ActivityCategory, string> = {
  auth: "Authentication",
  strategy: "Strategy",
  portfolio: "Portfolio",
  brokerage: "Brokerage",
  system: "System",
};

// Category colors for UI
export const CATEGORY_COLORS: Record<ActivityCategory, string> = {
  auth: "blue",
  strategy: "purple",
  portfolio: "green",
  brokerage: "orange",
  system: "gray",
};

// ==========================================
// User Admin Types and APIs
// ==========================================

export interface UserListItem {
  id: string;
  email: string;
  display_name: string;
  status: string;
  created_at: string;
  last_login_at?: string;
  is_admin: boolean;
}

export interface UserListResponse {
  users: UserListItem[];
  total: number;
}

export interface InvestmentInfo {
  id: string;
  strategy_id: string;
  strategy_name: string;
  invested_amount: string;
  current_value: string;
  total_return: string;
  total_return_pct: string;
  is_active: boolean;
  started_at: string;
  first_executed_at?: string;
}

export interface BrokerageAccountInfo {
  id: string;
  account_name: string;
  account_type: "paper" | "live";
  alpaca_account_id?: string;
  cash_balance: string;
  portfolio_value: string;
  status: string;
  is_default: boolean;
  created_at: string;
  last_synced_at?: string;
  investments?: InvestmentInfo[];
}

export interface UserRole {
  id: string;
  name: string;
  display_name: string;
  description?: string;
}

export interface ActivityStats {
  total_sign_ins: number;
  backtests_last_15_days: number;
  total_investments: number;
  active_investments: number;
}

export interface UserDetails {
  id: string;
  email: string;
  display_name: string;
  status: string;
  created_at: string;
  last_login_at?: string;
  email_verified_at?: string;
  mfa_enabled: boolean;
  mfa_method?: string;
  roles?: UserRole[];
  is_admin: boolean;
  brokerage_accounts: BrokerageAccountInfo[];
  activity_stats: ActivityStats;
}

// User Admin API Methods
export async function listUsers(): Promise<UserListResponse> {
  return adminRequest<UserListResponse>("/api/v1/admin/users");
}

export async function getUserDetails(userId: string): Promise<UserDetails> {
  return adminRequest<UserDetails>(`/api/v1/admin/users/${userId}`);
}

// ==========================================
// Admin Subscription Types and APIs
// ==========================================

export type SubscriptionStatus =
  | "active"
  | "expiring"
  | "renewing"
  | "payment_failed"
  | "suspended"
  | "canceled"
  | "paused"
  | "comped"
  | "none";

export interface AdminSubscription {
  id: string;
  user_id: string;
  plan_id: string;
  stripe_subscription_id?: string;
  stripe_customer_id?: string;
  status: SubscriptionStatus;
  current_period_start?: string;
  current_period_end?: string;
  cancel_at_period_end: boolean;
  canceled_at?: string;
  ended_at?: string;
  trial_start?: string;
  trial_end?: string;
  created_at: string;
  updated_at: string;
  plan_name: string;
  plan_display_name: string;
  plan_price_cents: number;
  user_email: string;
  user_display_name: string;
}

export interface AdminSubscriptionListResponse {
  subscriptions: AdminSubscription[];
  total: number;
  limit: number;
  offset: number;
}

export interface SubscriptionStats {
  [status: string]: number;
}

export interface RevenueBreakdown {
  total_revenue_cents: number;
  monthly_revenue_cents: number;
  mrr_cents: number;
  active_subscribers: number;
  churned_last_30_days: number;
  currency: string;
}

export interface SubscriptionOverride {
  id: string;
  user_id: string;
  override_type: string;
  reason: string;
  granted_by: string;
  expires_at?: string;
  is_active: boolean;
  notes?: string;
  created_at: string;
  revoked_at?: string;
  revoked_by?: string;
  user_email: string;
  user_display_name: string;
}

export interface OverrideListResponse {
  overrides: SubscriptionOverride[];
  total: number;
  limit: number;
  offset: number;
}

export type AuditActorType = "user" | "admin" | "system" | "stripe_webhook";

export interface SubscriptionAuditLogEntry {
  id: string;
  user_id: string;
  subscription_id?: string;
  action: string;
  actor_type: AuditActorType;
  actor_id?: string;
  previous_status?: SubscriptionStatus;
  new_status?: SubscriptionStatus;
  details: Record<string, unknown>;
  ip_address?: string;
  user_agent?: string;
  created_at: string;
  user_email: string;
  user_display_name: string;
  actor_email?: string;
}

export interface AuditLogListResponse {
  audit_logs: SubscriptionAuditLogEntry[];
  total: number;
  limit: number;
  offset: number;
}

export interface CompUserRequest {
  reason: string;
  expires_at?: string;
  notes?: string;
}

export interface ExtendSubscriptionRequest {
  days: number;
  reason: string;
}

export interface ActionWithReasonRequest {
  reason: string;
}

// Subscription Admin API Methods

export async function listAdminSubscriptions(
  status?: SubscriptionStatus,
  search?: string,
  limit: number = 20,
  offset: number = 0,
): Promise<AdminSubscriptionListResponse> {
  const params = new URLSearchParams();
  if (status) params.append("status", status);
  if (search) params.append("search", search);
  params.append("limit", limit.toString());
  params.append("offset", offset.toString());
  return adminRequest<AdminSubscriptionListResponse>(
    `/api/v1/admin/subscriptions?${params.toString()}`,
  );
}

export async function getAdminSubscriptionStats(): Promise<SubscriptionStats> {
  return adminRequest<SubscriptionStats>("/api/v1/admin/subscriptions/stats");
}

export async function getAdminRevenue(): Promise<RevenueBreakdown> {
  return adminRequest<RevenueBreakdown>("/api/v1/admin/subscriptions/revenue");
}

export async function getAdminUserSubscription(userId: string): Promise<AdminSubscription> {
  return adminRequest<AdminSubscription>(`/api/v1/admin/subscriptions/user/${userId}`);
}

export async function compUser(userId: string, req: CompUserRequest): Promise<{ message: string }> {
  return adminRequest<{ message: string }>(`/api/v1/admin/subscriptions/user/${userId}/comp`, {
    method: "POST",
    body: JSON.stringify(req),
  });
}

export async function pauseSubscription(
  userId: string,
  reason: string,
): Promise<{ message: string }> {
  return adminRequest<{ message: string }>(`/api/v1/admin/subscriptions/user/${userId}/pause`, {
    method: "POST",
    body: JSON.stringify({ reason }),
  });
}

export async function resumeSubscription(userId: string): Promise<{ message: string }> {
  return adminRequest<{ message: string }>(`/api/v1/admin/subscriptions/user/${userId}/resume`, {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export async function extendSubscription(
  userId: string,
  req: ExtendSubscriptionRequest,
): Promise<{ message: string }> {
  return adminRequest<{ message: string }>(`/api/v1/admin/subscriptions/user/${userId}/extend`, {
    method: "POST",
    body: JSON.stringify(req),
  });
}

export async function forceCancelSubscription(
  userId: string,
  reason: string,
): Promise<{ message: string }> {
  return adminRequest<{ message: string }>(`/api/v1/admin/subscriptions/user/${userId}/cancel`, {
    method: "POST",
    body: JSON.stringify({ reason }),
  });
}

export async function listAdminOverrides(
  activeOnly: boolean = false,
  limit: number = 20,
  offset: number = 0,
): Promise<OverrideListResponse> {
  const params = new URLSearchParams();
  if (activeOnly) params.append("active_only", "true");
  params.append("limit", limit.toString());
  params.append("offset", offset.toString());
  return adminRequest<OverrideListResponse>(
    `/api/v1/admin/subscriptions/overrides?${params.toString()}`,
  );
}

// ============================================
// Strategy Default Weights
// ============================================

export interface StrategyWeightItem {
  id: string;
  name: string;
  slug: string;
  default_weight: number;
}

export async function getStrategyWeights(): Promise<StrategyWeightItem[]> {
  const data = await adminRequest<{ strategies: StrategyWeightItem[] }>(
    `/api/v1/admin/strategies/weights`,
  );
  return data.strategies;
}

export async function updateStrategyWeight(id: string, weight: number): Promise<void> {
  await adminRequest(`/api/v1/admin/strategies/weights/${id}`, {
    method: "PUT",
    body: JSON.stringify({ weight }),
  });
}

export async function revokeOverride(overrideId: string): Promise<{ message: string }> {
  return adminRequest<{ message: string }>(
    `/api/v1/admin/subscriptions/overrides/${overrideId}`,
    { method: "DELETE" },
  );
}

export async function listAdminAuditLogs(
  action?: string,
  actorType?: AuditActorType,
  userId?: string,
  limit: number = 25,
  offset: number = 0,
): Promise<AuditLogListResponse> {
  const params = new URLSearchParams();
  if (action) params.append("action", action);
  if (actorType) params.append("actor_type", actorType);
  if (userId) params.append("user_id", userId);
  params.append("limit", limit.toString());
  params.append("offset", offset.toString());
  return adminRequest<AuditLogListResponse>(
    `/api/v1/admin/subscriptions/audit-log?${params.toString()}`,
  );
}

// Stripe mode toggle
export async function getStripeMode(): Promise<{ mode: string }> {
  return adminRequest<{ mode: string }>("/api/v1/admin/subscriptions/stripe/mode");
}

export async function setStripeMode(mode: "test" | "live"): Promise<{ mode: string; message: string }> {
  return adminRequest<{ mode: string; message: string }>("/api/v1/admin/subscriptions/stripe/mode", {
    method: "POST",
    body: JSON.stringify({ mode }),
  });
}

// ==========================================
// Promo Code Types and APIs
// ==========================================

export interface AdminPromoCode {
  id: string;
  code: string;
  description: string;
  discount_percent: string;
  starts_at: string;
  ends_at: string;
  is_active: boolean;
  is_expired: boolean;
  is_public: boolean;
  max_uses: number;
  times_used: number;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface AdminPromoCodeListResponse {
  promo_codes: AdminPromoCode[];
}

export interface CreatePromoCodeRequest {
  code: string;
  description: string;
  discount_percent: number;
  starts_at: string;
  ends_at: string;
  max_uses?: number;
  is_public?: boolean;
}

export async function listPromoCodes(): Promise<AdminPromoCodeListResponse> {
  return adminRequest<AdminPromoCodeListResponse>("/api/v1/admin/subscriptions/promos");
}

export async function createPromoCode(req: CreatePromoCodeRequest): Promise<{ message: string; promo_code: AdminPromoCode }> {
  return adminRequest<{ message: string; promo_code: AdminPromoCode }>("/api/v1/admin/subscriptions/promos", {
    method: "POST",
    body: JSON.stringify(req),
  });
}

export async function deactivatePromoCode(promoId: string): Promise<{ message: string }> {
  return adminRequest<{ message: string }>(`/api/v1/admin/subscriptions/promos/${promoId}`, {
    method: "DELETE",
  });
}

// ==========================================
// Admin Banner Types and APIs
// ==========================================

export interface AdminBannerConfig {
  enabled: boolean;
  type: "info" | "warning" | "critical";
  message: string;
  updated_at: string;
}

export interface AdminBannerUpdate {
  enabled: boolean;
  type: "info" | "warning" | "critical";
  message: string;
}

export async function getAdminBanner(): Promise<AdminBannerConfig> {
  return adminRequest<AdminBannerConfig>("/api/v1/banner");
}

export async function updateAdminBanner(update: AdminBannerUpdate): Promise<AdminBannerConfig> {
  return adminRequest<AdminBannerConfig>("/api/v1/admin/banner", {
    method: "PUT",
    body: JSON.stringify(update),
  });
}

